<template>
    <b-container fluid>
        <b-form-row>
            <b-col md="4" class="my-2">
                <b-input-group>
                    <b-form-input v-model="filter" placeholder="Type to filter the table." />
                    <b-input-group-append>
                        <b-button :disabled="!filter" @click="filter = ''">
                            Clear
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-col>
        </b-form-row>
        <b-table
            id="list-of-purchases"
            responsive
            striped
            hover
            caption-top
            sort-desc
            sort-by="state_datetime"
            stacked="sm"
            :fields="applicantColumns"
            :items="purchaseItems"
            :filter="filter"
            @row-clicked="selectPurchase"
        >
            <template v-slot:empty>
                <div>
                    There were no Purchases found for this Annual Plan. To create one return to your
                    <b-link :to="{name: 'budgets', params: {applicationId}}">
                        Annual Plan Budgets
                    </b-link>
                </div>
            </template>

            <template v-slot:emptyFiltered>
                <div>
                    There were no Purchases found for that filter criteria. To create one return to your
                    <b-link :to="{name: 'budgets', params: {applicationId}}">
                        Annual Plan Budgets
                    </b-link>
                </div>
            </template>

            <template v-slot:cell(cost_id) />

            <template v-slot:head(_actions)>
                <b-form-row class="actions-column">
                    <b-col>
                        <fa-icon fixed-width
                                 size="lg"
                                 title="Submit"
                                 :icon="['far','inbox-in']"
                        />
                    </b-col>

                    <b-col>
                        <fa-icon fixed-width
                                 size="lg"
                                 title="Delete"
                                 :icon="['far','trash-alt']"
                        />
                    </b-col>
                </b-form-row>
            </template>

            <template v-slot:cell(_actions)="row">
                <b-form-row class="text-right actions-column">
                    <b-col v-if="canSubmit(row.item)">
                        <confirmable-submit-action-button
                            v-b-tooltip.hover.auto
                            title="Submit this purchase"
                            :disabled="!row.item._actions.submit"
                            :actionable-item="row.item"
                            :icon-config="{
                                variant: 'submit',
                                classes: ['fa-fw', 'fa-lg'],
                            }"
                            :submit-words="{itemType: 'Purchase', action: 'Submit'}"
                            @submitConfirmed="onSubmitConfirm(row)"
                        />
                    </b-col>
                    <b-col v-else class="action-placeholder" />

                    <b-col v-if="canDelete(row.item)">
                        <confirmable-delete-action-button
                            v-b-tooltip.hover.auto
                            title="Delete this purchase"
                            :disabled="!row.item._actions.delete"
                            :actionable-item="row.item"
                            :icon-config="{
                                variant: 'delete',
                                classes: ['fa-fw', 'fa-lg'],
                            }"
                            :delete-words="{itemType: 'Purchase', action: 'Delete'}"
                            @deleteConfirmed="onDeleteConfirm(row)"
                        />
                    </b-col>
                    <b-col v-else class="action-placeholder" />
                </b-form-row>
            </template>

            <template slot="table-caption">
                <span>
                    Click a column header to sort the table or click on a row to view/edit the
                    Purchase details.
                </span>
            </template>
        </b-table>
    </b-container>
</template>

<script>
import { Arc } from '$Shared/@arc/vue-arc';

export default {
    name: 'PurchaseListTable',

    metaInfo() {
        return { title: 'Purchases' };
    },

    props: {
        applicationId: { type: [String, Number], required: true },
        purchaseItems: { type: Array, required: true },
        filteredBy: { type: String, default: null },
        costTypeLookup: { type: Function, default() { return {}; } },
        canSubmit: { type: Function, default() { return false; } },
        canDelete: { type: Function, default() { return false; } },
    },

    data() {
        return {
            purchaseListFields: [
                {
                    key: 'purchase_id',
                    label: 'ID',
                    sortable: true,
                },
                {
                    key: 'proposalTitle',
                    label: 'Activity Title',
                    sortable: true,
                    sortByFormatted: true,
                    tdAttr: (value, key, item) => {
                        return { title: `${value} (${item.cost_id})` };
                    },
                    formatter: (value, key, item) => {
                        const trimLength = 30;
                        const trimmed = value.length > trimLength
                            ? value.substring(0, (trimLength - 3)) + '...'
                            : value;

                        return trimmed || 'No Activity Title';
                    }
                },
                {
                    key: 'costType',
                    label: 'Cost Type',
                    sortable: true,
                    sortByFormatted: true,
                    formatter: (value) => this.costTypeLookup(value).category || 'Unknown'
                },
                {
                    key: 'costDescription',
                    label: 'Cost Desc.',
                    sortable: true,
                    sortByFormatted: true,
                    tdAttr: (value, key, item) => {
                        return { title: `${value} (${item.cost_id})` };
                    },
                    formatter: (value, key, item) => {
                        const trimLength = 30;
                        const trimmed = value.length > trimLength
                            ? value.substring(0, (trimLength - 3)) + '...'
                            : value;

                        return trimmed || 'No Cost Description';
                    }
                },
                {
                    key: 'total_cost',
                    label: 'Total',
                    sortable: true,
                    tdClass: 'money-column',
                    formatter: (value) => Arc.Money.asCurrency(value)
                },
                {
                    key: 'state',
                    label: 'Status',
                    sortable: true,
                    sortByFormatted: true,
                    formatter: (value) => value.current_state
                },
                {
                    key: 'state_datetime',
                    label: 'Updated',
                    sortable: true,
                    sortByFormatted: true,
                    formatter: (value) => (new Date(value)).toLocaleDateString(),
                },
                {
                    key: '_actions',
                    label: 'Actions',
                    sortable: false,
                    thClass: 'actions-header-2 text-right',
                },
            ],

            filter: null,
        };
    },

    computed: {
        applicantColumns() {
            return this.purchaseListFields;
        },
    },

    created() {
        this.filter = this.filteredBy;
    },

    methods: {
        selectPurchase(row) {
            this.$emit('selected-purchase', row);
            this.$root.$emit('bv::hide::popover');
        },

        onSubmitConfirm(row) {
            this.$emit('submitted-purchase', row);
            this.$root.$emit('bv::hide::popover');
        },

        onDeleteConfirm(row) {
            this.$emit('deleted-purchase', row);
            this.$root.$emit('bv::hide::popover');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '$StyleVars';

h4 {
    text-align: center;
}

h6 {
    text-align: center;
}

td.table-success {
    vertical-align: middle;
    text-align: center;
}

table#list-of-local-applications {
    vertical-align: middle;
    text-align: center;
}

#list-of-local-applications__caption_ {
    font-size: .75rem;
}

tr {
    cursor: pointer;
}

$action-button-width: 50px;
$estimated-description-column-width: calc(
    83vw - (11ch + 8ch + 22ch + (#{$action-button-width * 3}) + (16px * 3))
);

.table {
    .actions-column .col {
        padding-left: 0;
        padding-right: 0;
        white-space: nowrap;

        .action-placeholder {
            width: $action-button-width;
        }
    }

    .money-column {
        text-align: right;
    }

    /deep/ thead {
        th.description {
            width: calc(100% - (50px + 50px + 80px + 125px));
        }

        th.actions-header,
        th.actions-header-1,
        th.actions-header-2,
        th.actions-header-3,
        th.actions-header-4 {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            white-space: nowrap;

            .col {
                padding: 5px 9px;

                svg {
                    vertical-align: bottom;
                }
            }
        }

        th.actions-header-1 {
            width: ($action-button-width + 8px) * 1;
            min-width: ($action-button-width + 8px) * 1;
        }

        th.actions-header-2 {
            width: ($action-button-width + 8px) * 2;
            min-width: ($action-button-width + 8px) * 2;
        }

        th.actions-header-3 {
            width: ($action-button-width + 8px) * 3;
            min-width: ($action-button-width + 8px) * 3;
        }

        th.actions-header-4 {
            width: ($action-button-width + 8px) * 4;
            min-width: ($action-button-width + 8px) * 4;
        }
    }

    /deep/ tbody {
        tr:hover {
            cursor: pointer;
        }

        td {
            /* white-space: nowrap; */

            .description {
                position: relative;
                width: $estimated-description-column-width;

                &:before {
                    content: attr(title);
                    max-width: 100%;
                    overflow: hidden;
                    position: absolute;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                &:after {
                    content: attr(title);
                    display: inline-block;
                    height: 0;
                    overflow: hidden;
                }
            }
        }
    }
}

</style>
